/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * PerSubjectScoreDisplay - Per-subject SAT score display for reflective dashboard
 *
 * Displays current and predicted scores for Math and Reading & Writing separately,
 * with trend indicators and combined total.
 */

import PropTypes from 'prop-types'
import { TrendingUp, TrendingDown, Minus, Calculator, BookOpen } from 'lucide-react'

/**
 * Get score color class based on performance tier
 */
const getScoreColorClass = (score, maxScore = 800) => {
  if (!score) return 'text-gray-400'
  const minScore = maxScore === 1600 ? 400 : 200
  const percentile = (score - minScore) / (maxScore - minScore)
  if (percentile >= 0.75) return 'text-green-600 dark:text-green-400'
  if (percentile >= 0.5) return 'text-blue-600 dark:text-blue-400'
  if (percentile >= 0.25) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-gray-600 dark:text-gray-400'
}

/**
 * Get trend icon and styling
 */
const TrendIndicator = ({ trend, showChange = true }) => {
  if (!trend || trend.direction === 'stable') {
    return (
      <div className="flex items-center gap-1 text-gray-400 dark:text-gray-500">
        <Minus size={14} />
        {showChange && <span className="text-xs">stable</span>}
      </div>
    )
  }

  if (trend.direction === 'improving') {
    return (
      <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
        <TrendingUp size={14} />
        {showChange && trend.change > 0 && (
          <span className="text-xs font-medium">+{trend.change}</span>
        )}
      </div>
    )
  }

  if (trend.direction === 'declining') {
    return (
      <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
        <TrendingDown size={14} />
        {showChange && trend.change < 0 && (
          <span className="text-xs font-medium">{trend.change}</span>
        )}
      </div>
    )
  }

  // Mixed trends
  if (trend.direction === 'mixed_improving') {
    return (
      <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
        <TrendingUp size={14} />
        {showChange && trend.change !== 0 && (
          <span className="text-xs font-medium">
            {trend.change > 0 ? '+' : ''}
            {trend.change}
          </span>
        )}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1 text-gray-400">
      <Minus size={14} />
    </div>
  )
}

TrendIndicator.propTypes = {
  trend: PropTypes.shape({
    direction: PropTypes.string,
    change: PropTypes.number
  }),
  showChange: PropTypes.bool
}

/**
 * Individual score column component
 */
const ScoreColumn = ({
  label,
  icon: Icon,
  currentScore,
  predictedScore,
  trend,
  maxScore = 800,
  showCurrent = true,
  showPredicted = true,
  showTrend = true,
  isTotal = false,
  compact = false
}) => {
  const hasData = currentScore !== null || predictedScore !== null

  if (!hasData) {
    return (
      <div
        className={`flex flex-col items-center p-3 ${isTotal ? 'bg-primary-50 dark:bg-primary-900/20' : 'bg-gray-50 dark:bg-gray-700/50'} rounded-lg`}
      >
        <div className="flex items-center gap-1.5 mb-2">
          {Icon && <Icon size={compact ? 14 : 16} className="text-gray-400" />}
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            {label}
          </span>
        </div>
        <span className="text-lg text-gray-400 dark:text-gray-500">--</span>
        <span className="text-xs text-gray-400">No data</span>
      </div>
    )
  }

  return (
    <div
      className={`flex flex-col items-center p-3 ${isTotal ? 'bg-primary-50 dark:bg-primary-900/20' : 'bg-gray-50 dark:bg-gray-700/50'} rounded-lg`}
    >
      {/* Label with icon */}
      <div className="flex items-center gap-1.5 mb-2">
        {Icon && (
          <Icon
            size={compact ? 14 : 16}
            className={
              isTotal
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-gray-500 dark:text-gray-400'
            }
          />
        )}
        <span
          className={`text-xs font-medium uppercase tracking-wide ${isTotal ? 'text-primary-700 dark:text-primary-300' : 'text-gray-500 dark:text-gray-400'}`}
        >
          {label}
        </span>
      </div>

      {/* Current Score */}
      {showCurrent && currentScore !== null && (
        <div className="mb-1">
          <span className="text-xs text-gray-500 dark:text-gray-400 block text-center">
            Current
          </span>
          <span
            className={`${compact ? 'text-xl' : 'text-2xl'} font-bold ${getScoreColorClass(currentScore, maxScore)}`}
          >
            {currentScore}
          </span>
        </div>
      )}

      {/* Predicted Score */}
      {showPredicted && predictedScore !== null && (
        <div className={showCurrent ? 'mt-2' : ''}>
          <span className="text-xs text-gray-500 dark:text-gray-400 block text-center">
            Predicted
          </span>
          <span
            className={`${compact ? 'text-lg' : 'text-xl'} font-semibold ${getScoreColorClass(predictedScore, maxScore)}`}
          >
            {predictedScore}
          </span>
        </div>
      )}

      {/* Trend indicator */}
      {showTrend && trend && (
        <div className="mt-2">
          <TrendIndicator trend={trend} showChange={!compact} />
        </div>
      )}
    </div>
  )
}

ScoreColumn.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  currentScore: PropTypes.number,
  predictedScore: PropTypes.number,
  trend: PropTypes.object,
  maxScore: PropTypes.number,
  showCurrent: PropTypes.bool,
  showPredicted: PropTypes.bool,
  showTrend: PropTypes.bool,
  isTotal: PropTypes.bool,
  compact: PropTypes.bool
}

/**
 * PerSubjectScoreDisplay - Main component
 *
 * @param {Object} props
 * @param {Object} props.mathData - Math section data { currentScore, predictedScore, trend, confidence }
 * @param {Object} props.readingWritingData - R&W section data
 * @param {Object} props.combinedData - Combined totals data
 * @param {boolean} props.showCurrent - Whether to show current scores
 * @param {boolean} props.showPredicted - Whether to show predicted scores
 * @param {boolean} props.showTrend - Whether to show trend indicators
 * @param {boolean} props.showCombined - Whether to show combined total
 * @param {boolean} props.compact - Use compact layout
 * @param {string} props.className - Additional CSS classes
 */
const PerSubjectScoreDisplay = ({
  mathData,
  readingWritingData,
  combinedData,
  showCurrent = true,
  showPredicted = true,
  showTrend = true,
  showCombined = true,
  compact = false,
  className = ''
}) => {
  // Calculate combined if not provided
  const combinedCurrentScore =
    combinedData?.currentScore ??
    ((mathData?.currentScore || 0) + (readingWritingData?.currentScore || 0) || null)
  const combinedPredictedScore =
    combinedData?.predictedScore ??
    ((mathData?.predictedScore || 0) + (readingWritingData?.predictedScore || 0) || null)
  const combinedTrend = combinedData?.trend ?? {
    direction: 'stable',
    change: (mathData?.trend?.change || 0) + (readingWritingData?.trend?.change || 0)
  }

  return (
    <div className={`grid grid-cols-3 gap-3 ${className}`}>
      {/* Math Section */}
      <ScoreColumn
        label="Math"
        icon={Calculator}
        currentScore={mathData?.currentScore}
        predictedScore={mathData?.predictedScore}
        trend={mathData?.trend}
        maxScore={800}
        showCurrent={showCurrent}
        showPredicted={showPredicted}
        showTrend={showTrend}
        compact={compact}
      />

      {/* Reading & Writing Section */}
      <ScoreColumn
        label="R&W"
        icon={BookOpen}
        currentScore={readingWritingData?.currentScore}
        predictedScore={readingWritingData?.predictedScore}
        trend={readingWritingData?.trend}
        maxScore={800}
        showCurrent={showCurrent}
        showPredicted={showPredicted}
        showTrend={showTrend}
        compact={compact}
      />

      {/* Combined Total */}
      {showCombined && (
        <ScoreColumn
          label="Total"
          currentScore={combinedCurrentScore}
          predictedScore={combinedPredictedScore}
          trend={combinedTrend}
          maxScore={1600}
          showCurrent={showCurrent}
          showPredicted={showPredicted}
          showTrend={showTrend}
          isTotal={true}
          compact={compact}
        />
      )}
    </div>
  )
}

PerSubjectScoreDisplay.propTypes = {
  mathData: PropTypes.shape({
    currentScore: PropTypes.number,
    predictedScore: PropTypes.number,
    trend: PropTypes.object,
    confidence: PropTypes.number,
    confidenceLevel: PropTypes.string
  }),
  readingWritingData: PropTypes.shape({
    currentScore: PropTypes.number,
    predictedScore: PropTypes.number,
    trend: PropTypes.object,
    confidence: PropTypes.number,
    confidenceLevel: PropTypes.string
  }),
  combinedData: PropTypes.shape({
    currentScore: PropTypes.number,
    predictedScore: PropTypes.number,
    trend: PropTypes.object
  }),
  showCurrent: PropTypes.bool,
  showPredicted: PropTypes.bool,
  showTrend: PropTypes.bool,
  showCombined: PropTypes.bool,
  compact: PropTypes.bool,
  className: PropTypes.string
}

export default PerSubjectScoreDisplay
